import { resolvePoolLabel, useServiceLabel } from "@/api/local/labels";
import type { Pod, Pool, Replica, Service } from "@/api/types";
import { GLOBAL_FOCUS, type Focus } from "@/workloads/focus";
import { POD_TABS } from "@/workloads/scope";

import { DetailShell } from "./DetailShell";
import { PodDashboard } from "./pod/PodDashboard";
import { PodEvents } from "./pod/PodEvents";
import { PodInfo } from "./pod/PodInfo";
import { PodMetrics } from "./pod/PodMetrics";

interface Props {
  pod: Pod | null;
  replica: Replica;
  pool: Pool;
  service: Service;
  tab: string;
  setFocusAndTab: (focus: Focus, tab: string) => void;
}

export function PodDetail({
  pod,
  replica,
  pool,
  service,
  tab,
  setFocusAndTab,
}: Props) {
  const podLabel = pod
    ? `${pod.node_role}#${pod.node_index}`
    : `replica#${replica.id}`;
  const podKey = pod ? pod.id : replica.id;
  const serviceLabel = useServiceLabel(service.id, service.name);

  return (
    <DetailShell
      tabs={POD_TABS}
      activeTab={tab}
      onTabChange={(id) => setFocusAndTab({ type: "pod", id: podKey }, id)}
      navTitle={podLabel}
      navSubtitle="pod"
      breadcrumb={[
        {
          label: "BackStage",
          onClick: () => setFocusAndTab(GLOBAL_FOCUS, "dashboard"),
        },
        {
          label: serviceLabel,
          onClick: () =>
            setFocusAndTab({ type: "service", id: service.id }, "dashboard"),
        },
        {
          label: resolvePoolLabel(pool),
          onClick: () =>
            setFocusAndTab({ type: "pool", id: pool.id }, "dashboard"),
        },
        { label: podLabel },
      ]}
    >
      {renderTab(tab, pod, replica, pool, service)}
    </DetailShell>
  );
}

function renderTab(
  tab: string,
  pod: Pod | null,
  replica: Replica,
  pool: Pool,
  service: Service,
) {
  switch (tab) {
    case "metrics":
      return <PodMetrics pod={pod} replica={replica} pool={pool} />;
    case "info":
      return <PodInfo pod={pod} replica={replica} pool={pool} service={service} />;
    case "events":
      return <PodEvents replica={replica} pool={pool} service={service} />;
    case "dashboard":
    default:
      return <PodDashboard pod={pod} replica={replica} poolId={pool.id} />;
  }
}
